import {
  CalendarClock,
  CircleCheck,
  CircleSlash,
  ListChecks,
  MapPin,
  OctagonX,
  Tag,
  TriangleAlert,
  UserRound,
} from "lucide-react";
import {
  SEVERITY_TIERS,
  effectiveSeverity,
  isAnswered,
  isDismissed,
  isSeverityOverridden,
  isStopWork,
  latestFindings,
  type SeverityTier,
  type VisitDetail as VisitRecord,
} from "@taskiq/shared";
import { PersonAvatar } from "@/components/ui/Avatar";
import { SeverityChip } from "@/components/ui/Badge";
import { Card, CardBody } from "@/components/ui/Card";
import { Identity } from "@/components/ui/Identity";
import { LocationPreview } from "@/components/visit/LocationPreview";
import { formatDayTimeIn } from "@/lib/dates";
import { isDeletedUser } from "@/lib/users";
import { cn } from "@/lib/cn";

/**
 * The visit, answered in one card.
 *
 * ── Findings lead, provenance supports ──────────────────────────────────────
 *
 * This replaces a four-cell strip that gave "Attended by", "Started",
 * "Location" and "Findings" a quarter of the width each. That is the wrong
 * weighting for the two people who open this page. An EHS manager is deciding
 * whether this visit is acceptable, and a technician is checking that what they
 * recorded is what the record says - both of those are questions about *what
 * was found*, and the name and timestamp are what makes the answer admissible
 * rather than what the answer is.
 *
 * So the tally, the acknowledgement rate and the highest severity take the left
 * half at a size you can read across a desk, and the facts sit beside them as a
 * column of labelled lines. The breakdown by tier shares the tally's own row
 * rather than a row of its own, because it is context for the tally, not an
 * answer beside it, and giving it a second row was the whitespace this card
 * did not need.
 *
 * ── Which facts sit where ──────────────────────────────────────────────────
 *
 * All four facts in the right-hand column, in the order a reader needs them:
 * the job and the person first, because "2 findings" is not a readable
 * sentence until you know it was a fiber splice and who was standing there,
 * then *when* and *where* - the two facts that make the record admissible
 * rather than the two that say what it is.
 *
 * ── The total leads, the breakdown is one component under it ────────────────
 *
 * The tally is a headline, not a tile. Boxing it made it the first of four
 * cards standing side by side, and a total is not a peer of the parts it is the
 * sum of - reading it as one was the whole problem with that row.
 *
 * The three tiers under it are one object divided into three, not three objects
 * placed in a line: hairline rules between the cells, no box around any of
 * them. It takes the slack in the column rather than leaving a band of empty
 * card between the headline and the meters, which is the other half of why four
 * tiles read as loose furniture.
 *
 * All three tiers, every time, whether or not anything landed in one. A row
 * that only renders the tiers with hits changes shape from visit to visit, and
 * an empty stop cell is a *result* - it reads as one only when the cell is
 * there to be empty. The zeros are dimmed so the eye still goes to what was
 * found first.
 *
 * Each tier is a count over a `SeverityChip`. The chip is the one thing here
 * that may not be simplified: PRD §10 requires colour, icon and word together,
 * because roughly 8% of this workforce has a red/green deficiency and a hazard
 * breakdown they cannot read is worse than none.
 *
 * ── Two meters, side by side, and neither of them is a task ─────────────────
 *
 * "How many were dealt with" and "how much of the visit was run" are the two
 * questions the tally cannot answer. They are the same object twice - a label,
 * the figure, a bar - and they share a row under a rule: they measure the visit
 * rather than describe what it found, and the rule is what says so.
 *
 * Both are worded in the past, and neither carries a count of what is *left*.
 * "2 still open" on a completed visit reads as a queue somebody can work
 * through, and there is nothing to work through - the record closed when the
 * technician closed it. The bar shows the same gap without offering.
 *
 * ── Stop-work is its own line, never a colour ───────────────────────────────
 *
 * A technician refusing to work under a hazard is the single most consequential
 * thing this product records. It gets a row of its own when it happened and no
 * row at all when it did not, rather than being a redder shade of the tally.
 */
export function VisitSummary({ visit, timezone }: { visit: VisitRecord; timezone: string }) {
  // The newest attempt of each check, not every attempt of it. A technician who
  // re-shot a sweep has not found its hazards twice, and the cards below this
  // show the live answer - a summary that disagreed with them would be the
  // number a supervisor trusts least.
  const findings = visit.assessments.flatMap((assessment) => latestFindings(assessment.metadata));
  // "Answered" rather than "acknowledged": a finding the technician rejected as
  // a false positive, or filed as belonging to another job, has been dealt with.
  // Counting those as outstanding was what made this meter unreadable.
  const answered = findings.filter(isAnswered).length;
  const stopWork = findings.filter(isStopWork).length;
  const overridden = findings.filter(isSeverityOverridden).length;
  // Rejected as a false positive, or filed as another job's. Answered, on the
  // record, and counted nowhere in the tiers below - a hazard the person on site
  // says does not exist is not one this summary should be adding up.
  const dismissed = findings.filter(isDismissed).length;

  // Tallied at the severity each was FILED at, which is the technician's where
  // they overruled the analysis in either direction: a refusal counts as `stop`
  // whatever the model called it. `overridden` above is what keeps the downward
  // half of that honest.
  const byTier = SEVERITY_TIERS.map((tier) => ({
    tier,
    count: findings.filter(
      (finding) => !isDismissed(finding) && effectiveSeverity(finding) === tier
    ).length,
  }));

  const checks = visit.assessments.length;
  // `completed` only. A skipped check is a decision not to run one, and an
  // `in_progress` check is one somebody is standing in the middle of - counting
  // either as done let a visit read 4 of 4 with two checks nobody performed.
  // The phone's `Visit.modulesDone` says the same thing.
  const done = visit.assessments.filter((assessment) => assessment.status === "completed").length;

  const hasFix = visit.arrival_lat !== null && visit.arrival_lng !== null;

  return (
    <Card>
      <CardBody className="summary">
        <section className="summary__findings" aria-label="What the checks found">
          <div className="summary__lead">
            <h2 className="summary__headline">
              <span className="summary__count tnum">{findings.length}</span>
              <span className="summary__count-label">
                {findings.length === 1 ? "finding" : "findings"}
              </span>
            </h2>

            {findings.length === 0 ? (
              // A clean sweep is a result, and it says so in words rather than
              // leaving three zeros to be interpreted.
              <p className="summary__clear">No hazards were raised across the checks.</p>
            ) : (
              <ul className="summary__tiers">
                {byTier.map(({ tier, count }) => (
                  <TierCell key={tier} tier={tier} count={count} />
                ))}
              </ul>
            )}
          </div>

          {stopWork > 0 && (
            <p className="summary__stop">
              <OctagonX aria-hidden />
              Work was stopped on {stopWork} finding{stopWork === 1 ? "" : "s"}
            </p>
          )}

          {/* The tally above counts what the technician filed, so where they
              disagreed with the analysis the summary has to say so - otherwise
              a visit whose hazards were all talked down reads as a quiet one
              with nothing pointing at why. Not a warning and not a colour: a
              disagreement is a normal thing for this record to contain, and
              the per-finding rows carry both severities. */}
          {overridden > 0 && (
            <p className="summary__override">
              <TriangleAlert aria-hidden />
              {overridden} finding{overridden === 1 ? " was" : "s were"} filed below the severity
              the analysis gave {overridden === 1 ? "it" : "them"}
            </p>
          )}

          {/* Same reasoning as the line above, for the other way a tally can
              come out lower than the headline: these are out of the tiers
              entirely, so the row of counts has to say where they went. */}
          {dismissed > 0 && (
            <p className="summary__override">
              <CircleSlash aria-hidden />
              {dismissed} finding{dismissed === 1 ? " was" : "s were"} dismissed as not a hazard or
              not this job, and {dismissed === 1 ? "is" : "are"} not counted above
            </p>
          )}

          <div className="summary__progress">
            <h3 className="summary__section-label">
              <ListChecks aria-hidden /> Progress
            </h3>
            <div className="summary__meters">
              {findings.length > 0 && (
                <Meter
                  label="Answered by the technician"
                  done={answered}
                  total={findings.length}
                  tone={answered === findings.length ? "done" : "open"}
                />
              )}
              <Meter label="Checks completed" done={done} total={checks} />
            </div>
          </div>
        </section>

        <dl className="summary__facts">
          {/* What the job was and who ran it, leading the provenance column -
              the two facts that make the tally readable sit above the two
              that make the record admissible. */}
          <div className="summary__fact-pair">
            {visit.visit_type?.name && (
              <div className="summary__fact">
                <dt className="summary__fact-label">
                  <Tag aria-hidden /> Visit type
                </dt>
                <dd className="summary__fact-value">{visit.visit_type.name}</dd>
              </div>
            )}

            <div className="summary__fact">
              <dt className="summary__fact-label">
                <UserRound aria-hidden /> Attended by
              </dt>
              <dd className="summary__fact-value">
                <Identity
                  media={<PersonAvatar person={visit.technician} size="sm" />}
                  title={visit.technician?.full_name ?? "Unknown"}
                  subtitle={
                    visit.technician && !isDeletedUser(visit.technician.full_name)
                      ? visit.technician.email
                      : undefined
                  }
                />
              </dd>
            </div>
          </div>

          {/* Two halves of one fact - when it ran - so they share a row rather
              than stacking as peers of the name above and the address below.
              The phone already puts them side by side. */}
          <div className="summary__fact-pair">
            <div className="summary__fact">
              <dt className="summary__fact-label">
                <CalendarClock aria-hidden /> Started at
              </dt>
              <dd className="summary__fact-value tnum">
                {formatDayTimeIn(visit.started_at, timezone)}
              </dd>
            </div>

            <div className="summary__fact">
              <dt className="summary__fact-label">
                <CircleCheck aria-hidden /> Completed at
              </dt>
              {/* An open visit is a fact about the visit, not a missing value.
                  A dash here would read as data we failed to load. */}
              <dd
                className={cn(
                  "summary__fact-value",
                  !visit.ended_at && "summary__fact-value--open"
                )}
              >
                {visit.ended_at ? (
                  <span className="tnum">{formatDayTimeIn(visit.ended_at, timezone)}</span>
                ) : (
                  "Still open on the phone"
                )}
              </dd>
            </div>
          </div>

          {/* Where the phone was: the fix as an address (reverse-geocoded by the
              API, coordinates when it could not say) and as a map. Still nothing
              *derived* from it - no distance from the planned address and no
              accuracy radius: neither is something a reviewer can act on, and a
              "42 m out" on a record whose planned address is a street rather
              than a pole reads as a discrepancy when it is a rounding. */}
          <div className={cn("summary__fact", hasFix && "summary__fact--map")}>
            <dt className="summary__fact-label">
              <MapPin aria-hidden /> Location
            </dt>
            {/* Clamped to three lines: a rural address runs to five and the
                whole column grows to hold it. Two was one line short of a full
                street address - the clamp was firing on the ordinary case, not
                the pathological one. The full text is in the map dialog
                either way. */}
            <dd className="summary__fact-value summary__fact-value--clamp">
              {visit.arrival_address ??
                visit.appointment.location_address ??
                "No address on the appointment"}
            </dd>
            <dd className="summary__fact-note mono tnum">
              {hasFix
                ? `${visit.arrival_lat!.toFixed(5)}, ${visit.arrival_lng!.toFixed(5)}`
                : "No fix when the visit was opened"}
            </dd>
            {hasFix && (
              <dd className="summary__fact-map">
                <LocationPreview
                  lat={visit.arrival_lat!}
                  lng={visit.arrival_lng!}
                  address={visit.arrival_address}
                  plannedAddress={visit.appointment.location_address}
                />
              </dd>
            )}
          </div>
        </dl>
      </CardBody>
    </Card>
  );
}

/**
 * One tier and how many of it. The count is the point, so it is the loud half;
 * `SeverityChip` under it carries the tier's own colour, icon and word, which
 * is the rule this must not break to save a line.
 *
 * A tier nothing landed in is dimmed rather than dropped - see the note at the
 * top on why the row keeps its shape.
 */
function TierCell({ tier, count }: { tier: SeverityTier; count: number }) {
  return (
    <li className={cn("summary__tier", count === 0 && "summary__tier--empty")}>
      <span className="summary__tier-count tnum">{count}</span>
      <SeverityChip severity={tier} />
    </li>
  );
}

/**
 * A figure out of a total, with the bar that makes the gap visible.
 *
 * The label is the whole accessible name of the bar as well as its caption, so
 * a reader who never sees the fill gets the same sentence. `tone` is only for
 * the acknowledgement meter, where finishing the set is a verdict; how much of
 * a visit was run is a measurement, and colouring it would make a check that
 * was skipped for a good reason look like a failure.
 */
function Meter({
  label,
  done,
  total,
  tone,
}: {
  label: string;
  done: number;
  total: number;
  tone?: "open" | "done";
}) {
  return (
    <div className="summary__meter">
      <p className="summary__meter-head">
        {label}
        <span className="summary__meter-value tnum">
          {done} of {total}
        </span>
      </p>
      <div className="summary__bar" role="img" aria-label={`${label}: ${done} of ${total}`}>
        <span
          className={cn("summary__bar-fill", tone && `summary__bar-fill--${tone}`)}
          style={{ width: `${total === 0 ? 0 : (done / total) * 100}%` }}
        />
      </div>
    </div>
  );
}
